import React, { useState, useEffect, useRef } from 'react'
import { useLoadScript } from '@react-google-maps/api'
import './AutocompleteAddress.css' // Using the same CSS file
import { useTranslation } from 'react-i18next'

const libraries = ['places']
const europeBounds = {
	north: 71.344983,
	south: 35.929631,
	east: 49.860046,
	west: -25.022766,
}

const isLikelyAddress = (input) => {
	const hasNumbers = /\d/.test(input)
	const hasAddressPattern =
		/\d+\s+(?:rue|avenue|av|boulevard|boul|bd|route|rt|place|pl|chemin|ch|impasse|imp|street|st|road|rd|via|corso|piazza|strasse|str|gasse)/i.test(
			input
		)
	return hasNumbers || hasAddressPattern
}

export default function GooglePlacesAutoComplete({
	value,
	onChange,
	onPlaceSelect,
	onValueChange,
	placeholder = 'Entrez une adresse ou un lieu en Europe',
	disabled = false,
}) {
	
	const [inputValue, setInputValue] = useState('')
	const [predictions, setPredictions] = useState([])
	const [isFocused, setIsFocused] = useState(false)
	const [loading, setLoading] = useState(false)
	const [data, setData] = useState(value || {})
	const autocompleteService = useRef(null)
	const placesService = useRef(null)
	const containerRef = useRef(null)
	const debounceTimer = useRef(null)
   
	const { isLoaded } = useLoadScript({
		googleMapsApiKey: 'AIzaSyDxxReUJkCTHeB-DRS-tTj7cRMO-RGE6DY',
		libraries,
	})

	useEffect(() => {
		if (isLoaded && !autocompleteService.current) {
			autocompleteService.current =
				new window.google.maps.places.AutocompleteService()
			placesService.current = new window.google.maps.places.PlacesService(
				document.createElement('div')
			)
		}
	}, [isLoaded])

	// Update input value when value prop changes from parent
	useEffect(() => {
		// CORRECTION: Gérer le cas où value est null ou undefined
		if (value && value.label) {
			setInputValue(value.label)
			setData(value)
		} else if (value === null || value === undefined || (typeof value === 'object' && !value.label)) {
			// Réinitialiser complètement le composant quand value est null/undefined
			setInputValue('')
			setData({})
			setPredictions([])
		}
	}, [value])

	const handleInputChange = (e) => {
		const text = e.target.value
		setInputValue(text)

		// Clear existing timer
		if (debounceTimer.current) {
			clearTimeout(debounceTimer.current)
		}

		// CORRECTION: Réinitialiser data quand l'input est vide
		if (text === '') {
			setPredictions([])
			setData({})
			// Notifier le parent que la valeur est vide
			onValueChange && onValueChange(null)
			onChange && onChange(null)
			return
		}

		if (!autocompleteService.current || text.length < 3) {
			setPredictions([])
			setData({})
			return
		}

		setLoading(true)

		// Debounce the API call
		debounceTimer.current = setTimeout(() => {
			const request = {
				input: text,
				bounds: new window.google.maps.LatLngBounds(
					{ lat: europeBounds.south, lng: europeBounds.west },
					{ lat: europeBounds.north, lng: europeBounds.east }
				),
				strictBounds: true,
				types: [isLikelyAddress(text) ? 'address' : 'establishment'],
			}

			autocompleteService.current.getPlacePredictions(
				request,
				(preds, status) => {
					setLoading(false)
					if (
						status ===
						window.google.maps.places.PlacesServiceStatus.OK
					) {
						setPredictions(preds || [])
					} else {
						setPredictions([])
					}
				}
			)
		}, 300)
	}

	const handleSelect = (prediction) => {
		if (!placesService.current) return

		const request = {
			placeId: prediction.place_id,
			fields: [
				'geometry.location',
				'place_id',
				'formatted_address',
				'types',
			],
		}

		placesService.current.getDetails(request, (place) => {
			if (!place || !place.geometry) return

			const selectedPlace = {
				value: {
					place_id: place.place_id,
					description: prediction.description,
					latitude: place.geometry.location.lat(),
					longitude: place.geometry.location.lng(),
				},
				label: prediction.description,
			}

			setInputValue(prediction.description)
			setPredictions([])
			setData(selectedPlace)

			// Call both callback functions to match the original component
			onPlaceSelect && onPlaceSelect(selectedPlace)
			onValueChange && onValueChange(selectedPlace)
			onChange && onChange(selectedPlace)
		})
	}

	const handleFocus = () => {
		setIsFocused(true)
	}

	const handleBlur = () => {
		// Small delay to allow for clicking on suggestions
		setTimeout(() => {
			setIsFocused(false)
		}, 200)
	}

	// Get icon based on prediction type
	const getTypeIcon = (types) => {
		if (!types || types.length === 0) return '📍'

		const primaryType = types[0]
		if (primaryType === 'establishment') {
			return '🏢'
		} else if (primaryType === 'route') {
			return '🛣️'
		}
		return '📍'
	}

	// Component to show when no results are found
	const EmptyResultComponent = () => {
		// Only render the empty component when focused and input has sufficient length
		if (!isFocused || inputValue.length < 3 || data.label) return null

		return (
			<div className='empty-container'>
				<div className='empty-text'>
					Aucun résultat trouvé. Essayez un autre terme de recherche.
				</div>
			</div>
		)
	}

	if (!isLoaded) {
		return (
			<div className='autocomplete-container'>
				<input
					className='location-search-input'
					disabled={true}
					placeholder='Loading...'
				/>
			</div>
		)
	}

	const showEmpty =
		isFocused &&
		!loading &&
		predictions.length === 0 &&
		inputValue.length > 3

	return (
		<div className='autocomplete-container'>
			<div className='places-autocomplete-container' ref={containerRef}>
				<input
					type='text'
					value={inputValue}
					onChange={handleInputChange}
					onFocus={handleFocus}
					onBlur={handleBlur}
					placeholder={placeholder}
					disabled={disabled}
					className='location-search-input'
				/>

				{isFocused && (
					<div className='autocomplete-dropdown-container'>
						{loading && (
							<div className='loading-indicator'>Loading...</div>
						)}

						{predictions.map((prediction) => {
							// Format the prediction similar to the original component
							const mainText =
								prediction.structured_formatting?.main_text ||
								prediction.description.split(',')[0]
							const secondaryText =
								prediction.structured_formatting
									?.secondary_text ||
								prediction.description
									.substring(
										prediction.description.indexOf(',') + 1
									)
									.trim()

							const typeIcon = getTypeIcon(prediction.types)

							return (
								<div
									key={prediction.place_id}
									className='suggestion-item'
									onMouseDown={(e) => e.preventDefault()}
									onClick={() => handleSelect(prediction)}
									onMouseEnter={(e) =>
										e.target.classList.add(
											'suggestion-item--active'
										)
									}
									onMouseLeave={(e) =>
										e.target.classList.remove(
											'suggestion-item--active'
										)
									}
								>
									<div className='row-container'>
										<span className='suggestion-icon'>
											{typeIcon}
										</span>
										<div className='row-text-container'>
											<span className='main-text'>
												{mainText}
											</span>
											<span className='secondary-text'>
												{secondaryText}
											</span>
										</div>
									</div>
								</div>
							)
						})}

						{showEmpty && <EmptyResultComponent />}
					</div>
				)}
			</div>
		</div>
	)
}